import React, { useState, useEffect } from 'react';
import { ThemeProvider, createTheme, CssBaseline } from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import Paper from '@mui/material/Paper';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';

const API_ENDPOINT = process.env.REACT_APP_TRADE_API_ENDPOINT;
console.log("Calling API:", API_ENDPOINT);

function App() {
  const [data, setData] = useState([]);
  const [callClosingPrice, setCallClosingPrice] = useState('');
  const [putClosingPrice, setPutClosingPrice] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const darkTheme = createTheme({
    palette: {
      mode: 'dark',
    },
    typography: {
      fontFamily: '"Roboto", sans-serif',
      body1: {
        fontSize: '0.875rem', // Compact font
      },
      h6: {
        fontSize: '1rem', // Compact font for headings
      },
      h4: {
        fontSize: '1.5rem', // Slightly smaller header
      },
    },
  });

  // Fetch data from API endpoint
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const response = await fetch(API_ENDPOINT);
        if (!response.ok) {
          throw new Error('Failed to fetch data');
        }
        const result = await response.json();
        // Save original data to track changes
        const dataWithOriginalValues = result.map(item => ({
          ...item,
          originalStrike: item.strike_price_int,
          originalQuantity: item.quantity_int,
        }));
        setData(dataWithOriginalValues);
        setLoading(false);
      } catch (err) {
        setError(err.message);
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // Handle quantity change (increment/decrement)
  const handleQuantityChange = (id, newQuantity) => {
    setData((prevData) =>
      prevData.map((item) =>
        item._id === id
          ? { ...item, quantity_int: newQuantity, hasChanged: true }
          : item
      )
    );
  };

  // Handle strike price change
  const handleStrikePriceChange = (id, increment) => {
    setData((prevData) =>
      prevData.map((item) =>
        item._id === id
          ? { ...item, strike_price_int: item.strike_price_int + increment, hasChanged: true }
          : item
      )
    );
  };

  // Calculate gain/loss based on strike, quantity, and multiplier
  const calculateGainLoss = (strike, type, closingPrice, quantity, multiplier) => {
    const closingPriceNum = parseFloat(closingPrice);
    const strikePrice = parseFloat(strike);

    // Check if the option is in the money
    let isInTheMoney = false;

    if (type === 'CALL') {
      // Call option is in the money if the closing price is greater than the strike price
      isInTheMoney = closingPriceNum > strikePrice;
    } else if (type === 'PUT') {
      // Put option is in the money if the closing price is less than the strike price
      isInTheMoney = closingPriceNum < strikePrice;
    }

    // If the option is in the money, calculate the gain/loss, otherwise return 0
    if (isInTheMoney) {
      return (closingPriceNum - strikePrice) * multiplier * quantity;
    } else {
      return 0;
    }
  };

  // Group data by expiration date
  const groupByExpirationDate = (data) => {
    return data.reduce((acc, item) => {
      if (!acc[item.expiration_date]) {
        acc[item.expiration_date] = [];
      }
      acc[item.expiration_date].push(item);
      return acc;
    }, {});
  };

  // Sort expiration dates (ascending order)
  const expirationGroupedData = groupByExpirationDate(data);
  const sortedExpirationDates = Object.keys(expirationGroupedData).sort((a, b) => new Date(a) - new Date(b));

  // Calculate the total gain/loss for each section (Call and Put)
const calculateTotalGainLoss = (expirationDate, type, closingPrice) => {
  const totalGainLoss = expirationGroupedData[expirationDate]
    .filter(item => item.call_or_put === type)
    .reduce((total, item) => {
      const adjustedMultiplier = item.call_or_put === 'PUT'
        ? -1 * item.multiplier
        : item.multiplier;

      const gainLoss = calculateGainLoss(
        item.strike_price_int,
        type,
        closingPrice,
        item.quantity_int,
        adjustedMultiplier
      );

      return total + gainLoss;
    }, 0);

  return totalGainLoss;
};

  // Function to format numbers with commas and 2 decimal points
  const formatNumber = (number) => {
    return new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(number);
  };

  return (
    <ThemeProvider theme={darkTheme}>
      <CssBaseline />
      <Box sx={{ p: 2 }}>
        <Typography variant="h4" color="textPrimary" gutterBottom>
          Risk Dashboard
        </Typography>

        {/* Loading and error states */}
        {loading && <Typography variant="h6" color="textSecondary">Loading...</Typography>}
        {error && <Typography variant="h6" color="error">{error}</Typography>}

        {sortedExpirationDates.map((expirationDate) => (
          <Paper key={expirationDate} sx={{ mb: 4, padding: 2, borderRadius: 2, backgroundColor: 'background.default' }}>
            <Typography variant="h6" color="textSecondary">
              Expiration: {expirationDate}
            </Typography>

            <Box sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'center' }}>
              {/* Call Section */}
              <Box sx={{ flex: 1 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Box sx={{ flex: 1 }}>
                    <TextField
                      label="Call Closing Price"
                      variant="outlined"
                      size="small"
                      fullWidth
                      value={callClosingPrice}
                      onChange={(e) => setCallClosingPrice(e.target.value)}
                      type="number"
                      sx={{ input: { fontSize: '0.875rem' } }} // Smaller font for input
                    />
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="body1" color="textPrimary" sx={{ fontWeight: 'bold', textAlign: 'right' }}>
                      {formatNumber(calculateTotalGainLoss(expirationDate, 'CALL', callClosingPrice))}
                    </Typography>
                  </Box>
                </Box>
              </Box>

              {/* Put Section */}
              <Box sx={{ flex: 1 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Box sx={{ flex: 1 }}>
                    <TextField
                      label="Put Closing Price"
                      variant="outlined"
                      size="small"
                      fullWidth
                      value={putClosingPrice}
                      onChange={(e) => setPutClosingPrice(e.target.value)}
                      type="number"
                      sx={{ input: { fontSize: '0.875rem' } }} // Smaller font for input
                    />
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="body1" color="textPrimary" sx={{ fontWeight: 'bold', textAlign: 'right' }}>
                      {formatNumber(calculateTotalGainLoss(expirationDate, 'PUT', putClosingPrice))}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </Box>

            <Divider sx={{ mb: 2 }} /> {/* Divider between sections */}

            <Box sx={{ display: 'flex', gap: 2 }}>
              {/* Call Side Section */}
              <Box sx={{ flex: 1, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                <Typography variant="h6" color="textSecondary" sx={{ p: 1, backgroundColor: 'background.paper' }}>
                  Calls
                </Typography>
                <div style={{ height: 400, width: '100%' }}>
                  <DataGrid
                    rows={expirationGroupedData[expirationDate].filter(
                      (item) => item.call_or_put === 'CALL'
                    ).map((item) => ({
                      id: item._id,
                      strike: item.strike_price_int,
                      quantity: item.quantity_int,
                      gain_loss: calculateGainLoss(item.strike_price_int, 'CALL', callClosingPrice, item.quantity_int, item.multiplier),
                      originalStrike: item.originalStrike,
                      originalQuantity: item.originalQuantity,
                    }))}
                    columns={[
                      {
                        field: 'strike',
                        headerName: 'Strike Price',
                        width: 150,
                        renderCell: (params) => (
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              backgroundColor: params.row.strike !== params.row.originalStrike ? 'yellow' : 'transparent',
                              color: params.row.strike !== params.row.originalStrike ? 'red' : 'inherit',
                            }}
                          >
                            <IconButton
                              size="small"
                              onClick={() => handleStrikePriceChange(params.id, -5)}
                              sx={{ color: 'red' }}
                            >
                              <RemoveIcon />
                            </IconButton>
                            <Typography sx={{ marginX: 1, fontSize: '0.875rem' }}>
                              {params.row.strike}
                            </Typography>
                            <IconButton
                              size="small"
                              onClick={() => handleStrikePriceChange(params.id, 5)}
                              sx={{ color: 'red' }}
                            >
                              <AddIcon />
                            </IconButton>
                          </Box>
                        ),
                      },
                      {
                        field: 'quantity',
                        headerName: 'Quantity',
                        width: 150,
                        renderCell: (params) => {
                          return (
                            <Box
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                backgroundColor: params.row.quantity !== params.row.originalQuantity ? 'yellow' : 'transparent',
                                color: params.row.quantity !== params.row.originalQuantity ? 'red' : 'inherit',
                              }}
                            >
                              <IconButton
                                size="small"
                                onClick={() => handleQuantityChange(params.id, params.row.quantity - 1)}
                                sx={{ color: 'red' }}
                              >
                                <RemoveIcon />
                              </IconButton>
                              <Typography sx={{ marginX: 1, fontSize: '0.875rem' }}>
                                {params.row.quantity}
                              </Typography>
                              <IconButton
                                size="small"
                                onClick={() => handleQuantityChange(params.id, params.row.quantity + 1)}
                                sx={{ color: 'red' }}
                              >
                                <AddIcon />
                              </IconButton>
                            </Box>
                          );
                        },
                      },
                      {
                        field: 'gain_loss',
                        headerName: 'Gain/Loss',
                        width: 150,
                        renderCell: (params) => {
                          return formatNumber(params.row.gain_loss);
                        },
                      },
                    ]}
                    components={{
                      Footer: () => null,  // Remove the footer completely
                    }}
                    sx={{
                      '& .MuiDataGrid-columnHeaders': {
                        fontSize: '0.875rem',
                        backgroundColor: 'background.default',
                      },
                      '& .MuiDataGrid-cell': {
                        fontSize: '0.875rem',
                      },
                    }}
                  />
                </div>
              </Box>

              {/* Put Side Section */}
              <Box sx={{ flex: 1, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                <Typography variant="h6" color="textSecondary" sx={{ p: 1, backgroundColor: 'background.paper' }}>
                  Puts
                </Typography>
                <div style={{ height: 400, width: '100%' }}>
                  <DataGrid
                    rows={expirationGroupedData[expirationDate].filter(
                      (item) => item.call_or_put === 'PUT'
                    ).map((item) => ({
                      id: item._id,
                      strike: item.strike_price_int,
                      quantity: item.quantity_int,
                      gain_loss: calculateGainLoss(item.strike_price_int, 'PUT', putClosingPrice, item.quantity_int, (-1*item.multiplier)),
                      originalStrike: item.originalStrike,
                      originalQuantity: item.originalQuantity,
                    }))}
                    columns={[
                      {
                        field: 'strike',
                        headerName: 'Strike Price',
                        width: 150,
                        renderCell: (params) => (
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              backgroundColor: params.row.strike !== params.row.originalStrike ? 'yellow' : 'transparent',
                              color: params.row.strike !== params.row.originalStrike ? 'red' : 'inherit',
                            }}
                          >
                            <IconButton
                              size="small"
                              onClick={() => handleStrikePriceChange(params.id, -5)}
                              sx={{ color: 'red' }}
                            >
                              <RemoveIcon />
                            </IconButton>
                            <Typography sx={{ marginX: 1, fontSize: '0.875rem' }}>
                              {params.row.strike}
                            </Typography>
                            <IconButton
                              size="small"
                              onClick={() => handleStrikePriceChange(params.id, 5)}
                              sx={{ color: 'red' }}
                            >
                              <AddIcon />
                            </IconButton>
                          </Box>
                        ),
                      },
                      {
                        field: 'quantity',
                        headerName: 'Quantity',
                        width: 150,
                        renderCell: (params) => {
                          return (
                            <Box
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                backgroundColor: params.row.quantity !== params.row.originalQuantity ? 'yellow' : 'transparent',
                                color: params.row.quantity !== params.row.originalQuantity ? 'red' : 'inherit',
                              }}
                            >
                              <IconButton
                                size="small"
                                onClick={() => handleQuantityChange(params.id, params.row.quantity - 1)}
                                sx={{ color: 'red' }}
                              >
                                <RemoveIcon />
                              </IconButton>
                              <Typography sx={{ marginX: 1, fontSize: '0.875rem' }}>
                                {params.row.quantity}
                              </Typography>
                              <IconButton
                                size="small"
                                onClick={() => handleQuantityChange(params.id, params.row.quantity + 1)}
                                sx={{ color: 'red' }}
                              >
                                <AddIcon />
                              </IconButton>
                            </Box>
                          );
                        },
                      },
                      {
                        field: 'gain_loss',
                        headerName: 'Gain/Loss',
                        width: 150,
                        renderCell: (params) => {
                          return formatNumber(params.row.gain_loss);
                        },
                      },
                    ]}
                    components={{
                      Footer: () => null,  // Remove the footer completely
                    }}
                    sx={{
                      '& .MuiDataGrid-columnHeaders': {
                        fontSize: '0.875rem',
                        backgroundColor: 'background.default',
                      },
                      '& .MuiDataGrid-cell': {
                        fontSize: '0.875rem',
                      },
                    }}
                  />
                </div>
              </Box>
            </Box>
          </Paper>
        ))}
      </Box>
    </ThemeProvider>
  );
}

export default App;
